import React, { memo, useCallback, useEffect } from "react";
import { usePrevious } from "react-use";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useBottomMenu } from "../../hooks/useBottomMenu";
import { useDefinition } from "../../hooks/useDefinition";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useMessage } from "../../hooks/useMessage";
import { BottomMenu } from "./bottom-menu";

import { BackgroundColorPanel, BackgroundPatternPanel } from "../bottom-background-menus";
import { BottomRootMenu } from "../bottom-menus/bottom-root-menu";

import { BottomBackgroundMenu } from "../bottom-background-menus/bottom-background-menu";

import { DesignsPanel } from "../bottom-designs-menus";
import { LayoutsPanel } from "../bottom-layouts-menu";

import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useTextZoneDashboard } from "../../hooks/useTextZoneDashboard";
import { AlternativeProductsBottomMenuPanel } from "../bottom-menu-panels/alternative-products";
import { ProductOptionsBottomMenuPanel } from "../bottom-menu-panels/product-options";
import { OptionsBottomMenu } from "../bottom-menus/options";
import { getDefaultPathForZone } from "./bottom-menu-logic";
import { BottomMenuIds, BottomMenuPaths } from "./paths";

import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { StartDateSelectionBottomMenuPanel } from "../bottom-menu-panels/start-date-selection/start-date-selection-panel";
import { SymbolsPanelContainer as SymbolsListPanel } from "../bottom-menu-panels/symbols/symbols-list/symbols-list-panel-container";
import { SymbolsRootMenuContainer } from "../bottom-menu-panels/symbols/symbol-root-menu/symbols-root-menu-container";
import { useIsOverviewMode } from "../../hooks/useIsOverviewMode";

enum SliderType {
	Contrast = "contrast",
	Brightness = "brightness",
	Saturation = "saturation",
}

/** Created a function so import always happens with the webpackChunkName comment */
function getTextMenusAsync() {
	return import(
		/* webpackChunkName: "express-text-menus" */
		"../bottom-text-menus"
	).then((module) => module.default);
}

function getPhotoMenusAsync() {
	return import(
		/* webpackChunkName: "express-photo-menus" */
		"../bottom-photo-menus"
	).then((module) => module.default);
}

/**
 * The following code will keep the bottom menu in sync based on messages
 * (zone selection changes, commands that change selection, undo/redo, ...)
 * There is other code that will update the bottom menu based on a click
 */
function useUpdateBottomMenuOnMessage() {
	const { gotoPath, currentPath } = useBottomMenu();
	const {
		editor: { selection },
	} = useAppContext();
	const dashboard = useDashboardContext();
	const textDashboard = useTextZoneDashboard();
	const definition = useDefinition();
	const [isTransformMode] = useIsTransformMode();

	const previousDefinitionRef = usePrevious(definition);
	// Used useMessage instead of useEffect
	// Reason: We only want it to trigger on selection change, not if one of the dependencies of useEffect changes
	useMessage(
		useCallback(
			(message) => {
				if (message instanceof eXpress.core.DefinitionSwitchedMessage) {
					return;
				}

				// On selection change, try to keep current menu open
				if (
					eXpress.core.isSelectedZoneChanged(message) ||
					eXpress.core.isSelectedSpreadChanged(message) || // When going from no zone to other spread (no zone), the other spread can have other rules that the current menu cannot stay open)
					message instanceof eXpress.core.CurrentZoneTouchedMessage
				) {
					const zone = selection.getCurrentZone();
					const page = selection.getLogicalSelectedPage();
					const path = getDefaultPathForZone(
						currentPath,
						zone,
						page,
						{
							main: dashboard,
							text: textDashboard,
						},
						isTransformMode,
					);
					gotoPath(path);
				}
			},
			[currentPath, dashboard, gotoPath, selection, isTransformMode, textDashboard],
		),
	);

	// Load root items only the first time a definition is loaded
	useEffect(() => {
		if (!previousDefinitionRef && definition) {
			gotoPath(BottomMenuPaths.Root);
		}
	}, [gotoPath, definition, previousDefinitionRef]);
}

/** One button of the menu */
export const BottomMenuContainer = memo(function BottomMenuContainer() {
	const { localizedContent } = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();
	const { setComponentCreator } = useBottomMenu();
	const { createFatalErrorHandler } = useErrorHandling();

	const [isPreviewMode] = useIsPreviewMode();
	const [isOverviewMode] = useIsOverviewMode();

	useEffect(() => {
		/**
		 * A function that creates a bottom Menu
		 * We could make this later more modular by reversing dependency:
		 * Now container needs to know about all components, while dynamically loaded components could add themself as bottom menu
		 */
		const createComponentAsync = async (menuId: string) => {
			switch (menuId) {
				case BottomMenuIds.Root: {
					return <BottomRootMenu />;
				}

				case BottomMenuIds.Text: {
					const { BottomTextMenu } = await getTextMenusAsync();
					return <BottomTextMenu />;
				}
				case BottomMenuIds.TextValue: {
					const { TextValuePanel } = await getTextMenusAsync();
					return <TextValuePanel />;
				}
				case BottomMenuIds.TextSize: {
					const { TextSizePanel } = await getTextMenusAsync();
					return <TextSizePanel />;
				}
				case BottomMenuIds.TextAlignment: {
					const { TextAlignmentMenu } = await getTextMenusAsync();
					return <TextAlignmentMenu />;
				}
				case BottomMenuIds.FontFamilies: {
					const { FontFamiliesPanel } = await getTextMenusAsync();
					return <FontFamiliesPanel />;
				}
				case BottomMenuIds.TextStyle: {
					const { TextStylePanel } = await getTextMenusAsync();
					return <TextStylePanel />;
				}
				case BottomMenuIds.TextColor: {
					const { TextColorPanel } = await getTextMenusAsync();
					return <TextColorPanel />;
				}

				case BottomMenuIds.Photo: {
					const { BottomPhotoMenu } = await getPhotoMenusAsync();
					return <BottomPhotoMenu />;
				}

				case BottomMenuIds.ZoneDepth: {
					const { ZoneDepthPanel } = await getPhotoMenusAsync();
					return <ZoneDepthPanel />;
				}

				case BottomMenuIds.PhotoEditing: {
					const { BottomPhotoEditingMenu } = await getPhotoMenusAsync();
					return <BottomPhotoEditingMenu />;
				}

				case BottomMenuIds.PhotoTransform: {
					const { PhotoTransformPanel } = await getPhotoMenusAsync();
					return <PhotoTransformPanel />;
				}

				case BottomMenuIds.PhotoFilters: {
					const { PhotoFiltersPanel } = await getPhotoMenusAsync();
					return <PhotoFiltersPanel />;
				}

				case BottomMenuIds.PhotoBrightness: {
					const { PhotoEditingPanel } = await getPhotoMenusAsync();
					return <PhotoEditingPanel property={SliderType.Brightness} />;
				}

				case BottomMenuIds.PhotoSaturation: {
					const { PhotoEditingPanel } = await getPhotoMenusAsync();
					return <PhotoEditingPanel property={SliderType.Saturation} />;
				}

				case BottomMenuIds.PhotoContrast: {
					const { PhotoEditingPanel } = await getPhotoMenusAsync();
					return <PhotoEditingPanel property={SliderType.Contrast} />;
				}

				case BottomMenuIds.PhotoSelection: {
					const { PhotoSelectionPanel } = await getPhotoMenusAsync();
					return <PhotoSelectionPanel />;
				}

				case BottomMenuIds.Backgrounds: {
					return <BottomBackgroundMenu />;
				}

				case BottomMenuIds.BackgroundColor: {
					return <BackgroundColorPanel />;
				}

				case BottomMenuIds.BackgroundPattern: {
					return <BackgroundPatternPanel />;
				}

				case BottomMenuIds.Symbols: {
					return <SymbolsRootMenuContainer />;
				}

				case BottomMenuIds.SymbolsList: {
					return <SymbolsListPanel />;
				}

				case BottomMenuIds.Layouts: {
					return <LayoutsPanel />;
				}

				case BottomMenuIds.Designs: {
					return <DesignsPanel />;
				}

				case BottomMenuIds.Options: {
					return <OptionsBottomMenu />;
				}

				case BottomMenuIds.AlternativeProducts: {
					return <AlternativeProductsBottomMenuPanel />;
				}

				case BottomMenuIds.ProductOptions: {
					return <ProductOptionsBottomMenuPanel />;
				}

				case BottomMenuIds.DateSelection: {
					return <StartDateSelectionBottomMenuPanel />;
				}

				default: {
					return undefined;
				}
			}
		};

		setComponentCreator((menuId: string) =>
			busyBlocker.during(createComponentAsync(menuId)).catch((err: unknown) => {
				const error = new eXpress.GeneralException(
					"LoadBottomMenuException",
					`Error loading bottom menu '${menuId}'.`,
					err,
				);
				createFatalErrorHandler()(error);
				return undefined; // prevent UnhandledPromiseRejection
			}),
		);
	}, [busyBlocker, createFatalErrorHandler, localizedContent, setComponentCreator]);

	useUpdateBottomMenuOnMessage();

	if (isPreviewMode || isOverviewMode) return null;

	return <BottomMenu />;
});
